import { useQuery } from '@tanstack/react-query';
import { useApiClient } from '../api/ApiClientProvider';
import { useLocale } from '../i18n/LocaleProvider';

// Non-business foundation page demonstrating the end-to-end contract path:
// Zod contract -> OpenAPI -> generated client -> TanStack Query -> UI.
// Values are rendered exactly as the API sent them (no Date/Number parsing).
export function SystemStatusPage() {
  const client = useApiClient();
  const { messages } = useLocale();
  const info = useQuery({ queryKey: ['system', 'info'], queryFn: ({ signal }) => client.getSystemInfo({ signal }) });
  const readiness = useQuery({
    queryKey: ['health', 'ready'],
    queryFn: ({ signal }) => client.getReadiness({ signal }),
  });
  const session = useQuery({ queryKey: ['system', 'session'], queryFn: ({ signal }) => client.getSession({ signal }) });

  return (
    <main>
      <h1>{messages.systemStatus}</h1>

      <section aria-labelledby="service-info">
        <h2 id="service-info">{messages.serviceInformation}</h2>
        {info.isPending && <p role="status">{messages.loading}</p>}
        {(info.isError || (info.data && info.data.status !== 200)) && <p role="alert">{messages.apiUnavailable}</p>}
        {info.data?.status === 200 && (
          <dl>
            <dt>{messages.businessDate}</dt>
            <dd data-testid="business-date">{info.data.body.businessDate}</dd>
            <dt>{messages.serverTime}</dt>
            <dd data-testid="server-time" dir="ltr">
              {info.data.body.serverTime}
            </dd>
            <dt>{messages.contractVersion}</dt>
            <dd data-testid="contract-version">{info.data.body.contractVersion}</dd>
          </dl>
        )}
      </section>

      <section aria-labelledby="readiness">
        <h2 id="readiness">{messages.readiness}</h2>
        {readiness.isPending && <p role="status">{messages.loading}</p>}
        {readiness.isError && <p role="alert">{messages.apiUnavailable}</p>}
        {readiness.data && (
          <p data-testid="readiness">{readiness.data.status === 200 ? messages.ready : messages.notReady}</p>
        )}
      </section>

      <section aria-labelledby="session">
        <h2 id="session">{messages.session}</h2>
        {session.isPending && <p role="status">{messages.loading}</p>}
        {session.isError && <p role="alert">{messages.apiUnavailable}</p>}
        {session.data?.status === 401 && <p data-testid="session">{messages.signedOut}</p>}
        {session.data?.status === 200 && <p data-testid="session">{messages.signedInAs}</p>}
      </section>
    </main>
  );
}
